/**
 * TeamBlueprintPage — multi-person + project-context Blueprint generation.
 * Exercises Layer 2T → Layer 4 chain. Performance target: <60s end-to-end.
 */

import { useState, startTransition } from "react";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark, SectionHeader, Callout, SeriesBanner, AuditFooter, MarkdownV2, Chip, PrintToPdfButton, BlueprintJobView, RecentBlueprintsPanel,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";
import TeamEventLogPanel from "../components/TeamEventLogPanel.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const TEAM_PRESETS = [
  { label: "Who should I win first?",                 preset_text: "Given this room, who should I win first and why?" },
  { label: "Who really controls the yes?",            preset_text: "Who actually controls the decision here, regardless of title?" },
  { label: "Who's the hidden blocker?",               preset_text: "Who's most likely to quietly block this, and how do I defuse them?" },
  { label: "Smallest move this week",                 preset_text: "What's the smallest move I can make this week to move this group toward yes?" },
  { label: "How should I sequence the room?",         preset_text: "What order should I work these people in to build momentum?" },
  { label: "If this stalls, why?",                    preset_text: "If this stalls or dies, what's the most likely reason?" },
];

const USE_CASES = [
  { value: "buying_committee",     label: "Buying committee" },
  { value: "account_stakeholders", label: "Account / stakeholder map" },
  { value: "leadership_team",      label: "Leadership team" },
  { value: "panel",                label: "Panel / interview loop" },
  { value: "board",                label: "Board or investor group" },
  { value: "multi_party_negotiation", label: "Multi-party negotiation" },
  { value: "partnership",          label: "Partnership / alliance group" },
  { value: "offsite_prep",         label: "Offsite / retreat prep" },
  { value: "other",                label: "Other (free text)" },
];

export default function TeamBlueprintPage() {
  const { session } = useSession();
  const [useCase, setUseCase] = useState("project_staffing");
  const [projectName, setProjectName] = useState("");
  const [goals, setGoals] = useState("");
  const [stakes, setStakes] = useState("");
  const [memberMentions, setMemberMentions] = useState([]);
  const [memberText, setMemberText] = useState("");
  const [viewerMode, setViewerMode] = useState("participant");
  const [selfMentions, setSelfMentions] = useState([]);
  const [selfText, setSelfText] = useState("");
  const [localError, setLocalError] = useState(null);

  // Tier 2 (background+polling) per /shared/V1_OM_TeamBlueprint_Background_Polling_Relock.md
  // (locked 2026-05-07). Backend was already Tier 2 from #50; frontend
  // migrated 2026-05-09 (#77) to match — was still on useStreamingBlueprint
  // which would hit the V2 streaming runtime cap on long Team briefs.
  const [jobPayload, setJobPayload] = useState(null);
  const [blueprint, setBlueprint] = useState(null);

  const generate = async () => {
    if (memberMentions.length < 3) {
      setLocalError("Room Blueprints need at least 3 people.");
      return;
    }
    if (memberMentions.length > 8) {
      setLocalError("Room Blueprints support up to 8 people. For a bigger group, focus on the 8 who matter most to the decision.");
      return;
    }
    if (!projectName.trim()) {
      setLocalError("A room / deal name is required.");
      return;
    }
    setLocalError(null);
    setJobPayload({
      project_context: {
        use_case_template: useCase,
        project_name: projectName,
        goals,
        stakes,
        viewer_profile_id: selfMentions[0]?.profile_id || null,
      },
      member_profile_ids: memberMentions.map((m) => m.profile_id),
      viewer_mode: viewerMode,
    });
  };

  const retryJob = () => {
    if (!jobPayload) return;
    const payload = jobPayload;
    setJobPayload(null);
    setLocalError(null);
    setTimeout(() => setJobPayload(payload), 0);
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to generate a Room Blueprint.</p>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0} />
      </div>

      {jobPayload && !blueprint ? (
        <BlueprintJobView
          bearerToken={session.access_token}
          surfaceLabel="Room Blueprint"
          onRetry={retryJob}
          start={async () => {
            const res = await fetch("/.netlify/functions/team-blueprint-start", {
              method: "POST",
              headers: {
                "content-type": "application/json",
                authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify(jobPayload),
            });
            const respBody = await parseJsonResponse(res);
            if (!res.ok) {
              return { ok: false, error: respBody.error || `Failed to start (HTTP ${res.status})` };
            }
            return {
              ok: true,
              job_id: respBody.job_id,
              estimated_total_seconds: respBody.estimated_total_seconds,
            };
          }}
          onComplete={(_blueprintId, jobRow) => {
            const bp = jobRow?.blueprint;
            if (bp) {
              startTransition(() => {
                setBlueprint({
                  blueprint_id: bp.blueprint_id,
                  blueprint_type: "team_blueprint",
                  blueprint_markdown: bp.rendered_markdown,
                  hedge_phrases: bp.hedge_phrases || [],
                  jargon_scan_passed: bp.jargon_scan_passed,
                  engine_version: bp.layer_4_engine_version,
                  // Pulled from blueprints.mode_variant (set in team-blueprint-start.js
                  // to project_context.use_case_template). Without this the
                  // SeriesBanner rendered "TEAM BLUEPRINT · undefined" since
                  // bp.use_case_template was missing on the post-Tier-2 mapping.
                  use_case_template: bp.mode_variant || jobRow?.mode_variant || null,
                });
              });
            }
          }}
        />
      ) : !blueprint ? (
        <div>
          <RecentBlueprintsPanel
            type="team"
            bearerToken={session.access_token}
          />
          <SectionHeader index={1} title="New Room Blueprint" />

          <Callout.Tip>
            3–8 people. Generation typically takes 30–60s. Read the room — who decides, who blocks, who to win first — before you walk in.
          </Callout.Tip>

          <div style={{ marginTop: 14, marginBottom: 4, fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "var(--ppr-typography-font-size-sm)", color: "var(--ppr-color-text-secondary)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--ppr-color-text-primary)" }}>For example:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              <li>A six-person <strong>buying committee</strong> — who owns the budget, who's the blocker, who to win first.</li>
              <li>A <strong>renewal</strong> where your champion left and new faces inherited the account.</li>
              <li>An <strong>RFP final round</strong> — which seat to aim your strongest proof at.</li>
              <li>A <strong>board or investor</strong> pitch — who drives the call and who'll grill you.</li>
              <li>A client's stuck <strong>leadership team</strong> you've been brought in to align.</li>
              <li>A <strong>multi-party negotiation</strong> — who leads each side and who you can peel off.</li>
            </ul>
          </div>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            <Field label="Use case template">
              <select value={useCase} onChange={(e) => setUseCase(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                {USE_CASES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </Field>

            <Field label="Room / deal name">
              <input value={projectName} onChange={(e) => setProjectName(e.target.value)} style={inputStyle} placeholder="Acme renewal committee" />
            </Field>

            <Field label="Your goal in this room (optional, high-value)">
              <textarea value={goals} onChange={(e) => setGoals(e.target.value)} rows={3} style={inputStyle} />
            </Field>

            <Field label="Stakes (what happens if this goes wrong?)">
              <textarea value={stakes} onChange={(e) => setStakes(e.target.value)} rows={2} style={inputStyle} />
            </Field>

            <Field label={`People in the room (mention with @) — ${memberMentions.length} so far`}>
              <MentionAutocomplete
                value={memberText}
                onChange={setMemberText}
                mentions={memberMentions}
                onMentionsChange={setMemberMentions}
                placeholder="@Sarah @Marcus @Priya @Devon @Jules @Sam"
                rows={3}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="Viewer mode">
              <div style={{ display: "flex", gap: 8 }}>
                <Chip primary={viewerMode === "participant"} onClick={() => setViewerMode("participant")}>Participant (I'm in the room)</Chip>
                <Chip primary={viewerMode === "observer"} onClick={() => setViewerMode("observer")}>Outside party (selling / advising in)</Chip>
              </div>
            </Field>

            <Field label="Your profile (optional) — adds a “who you’ll click with” read">
              <MentionAutocomplete
                value={selfText}
                onChange={setSelfText}
                mentions={selfMentions}
                onMentionsChange={setSelfMentions}
                placeholder="@You — mention your own saved profile"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            {/* Generation mode toggle removed 2026-05-09 (#77). Tier 2
                background+polling is the only path — sync would 504 and
                streaming would hit the V2 runtime cap on long Team briefs. */}

            {localError && <ErrorBox error={localError} />}

            <button
              onClick={generate}
              style={terminalActionButtonStyle}
            >
              Generate Team Blueprint
            </button>
          </div>
        </div>
      ) : (
        <RenderedBlueprint
          blueprint={blueprint}
          bearerToken={session.access_token}
          memberFirstNames={memberMentions.map((m) => m.display_name).filter(Boolean)}
        />
      )}
    </div>
  );
}

function RenderedBlueprint({ blueprint, bearerToken, memberFirstNames }) {
  return (
    <article>
      <SeriesBanner type={blueprint.use_case_template ? `Room Blueprint · ${blueprint.use_case_template}` : "Room Blueprint"} dateLabel={new Date().toISOString().slice(0, 10)} />
      <div style={{ marginTop: 28 }}>
        <h1 style={titleStyle}>{blueprint.title}</h1>
        <div style={{ display: "flex", gap: 14, fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-muted)", marginTop: 12, flexWrap: "wrap" }}>
          <span>{blueprint.team_size} members</span>
          <span>{blueprint.alliance_count} alliances</span>
          <span>{blueprint.fault_line_count} fault lines</span>
          <span>{blueprint.pre_mortem_count} pre-mortem failure modes</span>
          {blueprint.shared_adversary_alignment_count !== undefined && (
            <span>shared adversary: {blueprint.shared_adversary_alignment_count}/{blueprint.shared_adversary_total_members}</span>
          )}
          {blueprint.lite_member_count > 0 && <span>Lite members: {blueprint.lite_member_count}</span>}
        </div>
      </div>

      {blueprint.internal_pointed_trap_flagged && (
        <Callout.WatchOut>
          <strong>Internal-pointed trap detected.</strong> This team's strongest available adversary is internal — what you're seeing is organizational dysfunction, not motivation. Don't operationalize it.
        </Callout.WatchOut>
      )}

      {/* MarkdownV2 with subject-name auto-bold (#80) — names threaded
          in from the parent's form-state memberMentions via the
          memberFirstNames prop. (Earlier wire used `memberMentions`
          directly inside this sub-component, where it's not in
          scope — fixed 2026-05-09.) */}
      <MarkdownV2
        source={blueprint.blueprint_markdown}
        subjectNames={memberFirstNames || []}
      />

      <TeamEventLogPanel
        blueprintId={blueprint.blueprint_id}
        bearerToken={bearerToken}
      />

      <BlueprintChat
        blueprintId={blueprint.blueprint_id}
        blueprintType="team"
        presetPrompts={TEAM_PRESETS}
        bearerToken={bearerToken}
      />

      <AuditFooter
        engineVersion={blueprint.engine_version}
        jargonScanPassed={true}
        blueprintId={blueprint.blueprint_id}
      />

      {/* Print-to-PDF (#73). */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-4xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 8px",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
